import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../lib/api';
const API = API_BASE;
const PAGE_SIZE = 10;

function hdrs() {
  const t = localStorage.getItem('retailcliq_token') || sessionStorage.getItem('retailcliq_token') || '';
  return { 'Content-Type': 'application/json', Authorization: t ? `Bearer ${t}` : '' };
}

/**
 * ERP-style GetInfo lookup popup with server-side pagination (10/page).
 * Mode A) tableName  → GET /api/inventory/getinfo/table/:tableName
 * Mode B) screenId + controlId → GET /api/inventory/getinfo/screen/:s/control/:c
 */
export default function GetInfoPopup({
  title, tableName, screenId, controlId,
  compId = 'C001', onSelect, onClose
}) {
  const [data, setData]             = useState([]);
  const [columns, setColumns]       = useState([]);
  const [meta, setMeta]             = useState({ valueMember: '', displayMember: '' });
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [searchCol, setSearchCol]   = useState('');
  const [searchVal, setSearchVal]   = useState('');
  const [localFilter, setLocalFilter] = useState('');
  const [useDefault, setUseDefault] = useState(false);
  const [autoLoad, setAutoLoad]     = useState(true);
  const [predictive, setPredictive] = useState(false);
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [pages, setPages]           = useState(1);
  const [activeRow, setActiveRow]   = useState(0);
  const searchRef  = useRef(null);
  const gridRef    = useRef(null);
  const useScreenMode = !!(screenId && controlId);

  const buildUrl = useCallback((q = '', col = '', pg = 1) => {
    const params = new URLSearchParams({ compId, page: pg, limit: PAGE_SIZE });
    if (q)   params.set('q', q);
    if (col) params.set('field', col);
    if (useScreenMode)
      return `${API}/api/inventory/getinfo/screen/${encodeURIComponent(screenId)}/control/${encodeURIComponent(controlId)}?${params}`;
    return `${API}/api/inventory/getinfo/table/${encodeURIComponent(tableName)}?${params}`;
  }, [screenId, controlId, tableName, compId, useScreenMode]);

  const load = useCallback(async (q = '', col = '', pg = 1) => {
    setLoading(true); setError('');
    try {
      const r = await fetch(buildUrl(q, col, pg), { headers: hdrs() });
      const j = await r.json();
      if (j.success) {
        setData(j.data || []);
        setTotal(j.total || 0);
        setPages(j.pages || 1);
        setPage(pg);
        setActiveRow(0);
        const cols = j.columns || [];
        setColumns(cols);
        setMeta({ valueMember: j.valueMember || '', displayMember: j.displayMember || '' });
        if (cols.length && !searchCol)
          setSearchCol(cols.find(c => c.isDisplay)?.field || cols[0]?.field || '');
      } else {
        setError(j.error || j.message || 'Failed to load');
        setData([]);
      }
    } catch (e) { setError(e.message); setData([]); }
    setLoading(false);
  }, [buildUrl, searchCol]);

  useEffect(() => { load(); }, []);
  useEffect(() => { setTimeout(() => searchRef.current?.focus(), 60); }, []);

  const handleSearch = (pg = 1) => { setPage(1); load(searchVal, searchCol, pg); };
  const goPage = (pg) => { if (pg < 1 || pg > pages) return; load(searchVal, searchCol, pg); };

  const filtered = localFilter
    ? data.filter(row => columns.some(c => String(row[c.field] ?? '').toLowerCase().includes(localFilter.toLowerCase())))
    : data;

  const handlePick = (row) => { onSelect(row, columns, meta); onClose(); };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter')  { if (e.target.tagName === 'INPUT' && e.target !== gridRef.current) { handleSearch(); return; } if (filtered[activeRow]) handlePick(filtered[activeRow]); }
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveRow(r => Math.min(r + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveRow(r => Math.max(r - 1, 0)); }
    if (e.key === 'PageDown')  goPage(page + 1);
    if (e.key === 'PageUp')    goPage(page - 1);
  };

  const modeLabel = useScreenMode ? `Screen ${screenId} / Ctrl ${controlId}` : tableName;
  const startRow  = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRow    = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="gi-overlay" onMouseDown={onClose} onKeyDown={handleKeyDown}>
      <div className="gi-popup" onMouseDown={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="gi-header">
          <span className="gi-title">{title}</span>
          <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 6 }}>{modeLabel}</span>
          <button className="gi-close-btn" onClick={onClose} title="Close (Esc)">✕</button>
        </div>

        {/* ── Search row ── */}
        <div className="gi-search-row">
          <select className="gi-col-sel" value={searchCol} onChange={e => { setSearchCol(e.target.value); if (searchVal) load(searchVal, e.target.value, 1); }}>
            {columns.map(c => <option key={c.field} value={c.field}>{c.header}</option>)}
          </select>
          <span className="gi-like-label">Like =</span>
          <input
            ref={searchRef}
            className="gi-search-inp"
            value={searchVal}
            onChange={e => { setSearchVal(e.target.value); if (predictive && e.target.value.length >= 2) load(e.target.value, searchCol, 1); }}
            onKeyDown={e => e.key === 'Enter' && handleSearch(1)}
            placeholder="Search value…"
          />
          <button className="gi-btn gi-arrow-btn" onClick={() => handleSearch(1)}>→</button>
          <button className="gi-btn gi-srch-btn" onClick={() => handleSearch(1)}>Search</button>
          <button className="gi-btn gi-f2-btn" onClick={() => handleSearch(1)}>F2</button>
        </div>

        {/* ── Options row ── */}
        <div className="gi-opts-row">
          <label className="gi-opt-lbl">
            <input type="checkbox" checked={useDefault} onChange={e => setUseDefault(e.target.checked)} />
            Use as default
          </label>
          <label className="gi-opt-lbl">
            <input type="checkbox" checked={autoLoad} onChange={e => { setAutoLoad(e.target.checked); if (e.target.checked) load('', searchCol, 1); }} />
            Auto Load {title}
          </label>
          <label className="gi-opt-lbl">
            <input type="checkbox" checked={predictive} onChange={e => setPredictive(e.target.checked)} />
            Use Predictive Searching
          </label>
        </div>

        {/* ── Quick filter ── */}
        <div className="gi-qfilter-row">
          <input
            className="gi-qfilter-inp"
            value={localFilter}
            onChange={e => setLocalFilter(e.target.value)}
            placeholder="Quick filter current page…"
          />
          <span className="gi-row-count">
            {total > 0 ? `${startRow}–${endRow} of ${total}` : `${filtered.length} record${filtered.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* ── Grid ── */}
        <div className="gi-grid-wrap" ref={gridRef} tabIndex={-1}>
          {loading ? (
            <div className="gi-state-msg gi-loading"><span className="gi-spinner" /> Loading {title}…</div>
          ) : error ? (
            <div className="gi-state-msg gi-error">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="gi-state-msg gi-empty">No records found</div>
          ) : (
            <table className="gi-table">
              <thead>
                <tr>{columns.map(c => <th key={c.field} className="gi-th">{c.header}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr
                    key={i}
                    className={`gi-tr${i === activeRow ? ' gi-tr-active' : ''}`}
                    onClick={() => handlePick(row)}
                    onMouseEnter={() => setActiveRow(i)}
                    onKeyDown={e => e.key === 'Enter' && handlePick(row)}
                    tabIndex={0}
                  >
                    {columns.map(c => <td key={c.field} className="gi-td">{row[c.field] ?? ''}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination bar ── */}
        {pages > 0 && (
          <div className="gi-pager">
            <button className="gi-pg-btn" disabled={page <= 1} onClick={() => goPage(1)} title="First">«</button>
            <button className="gi-pg-btn" disabled={page <= 1} onClick={() => goPage(page - 1)} title="Prev (PageUp)">‹</button>
            <span className="gi-pg-info">
              Page&nbsp;
              <input
                className="gi-pg-input"
                type="number" min={1} max={pages}
                value={page}
                onChange={e => { const v = parseInt(e.target.value); if (v >= 1 && v <= pages) goPage(v); }}
                style={{ width: 36, textAlign: 'center', padding: '1px 4px', border: '1px solid #cbd5e1', borderRadius: 3, fontSize: 11 }}
              />
              &nbsp;/ {pages}
            </span>
            <button className="gi-pg-btn" disabled={page >= pages} onClick={() => goPage(page + 1)} title="Next (PageDown)">›</button>
            <button className="gi-pg-btn" disabled={page >= pages} onClick={() => goPage(pages)} title="Last">»</button>
            <span className="gi-pg-size">{PAGE_SIZE} / page</span>
            {loading && <span className="gi-pg-loading">…</span>}
          </div>
        )}

      </div>
    </div>
  );
}
